import React from "react";
import { Workflow, ShieldAlert, Crosshair, Gauge } from "lucide-react";

const FRAMEWORKS = [
  {
    abbr: "PASTA",
    icon: Workflow,
    name: "Process for Attack Simulation & Threat Analysis",
    role: "Threat-modeling methodology",
    accent: "#c4b5fd",
    desc: "A comprehensive seven-step, risk-centric methodology. PASTA walks from business objectives and technical scope through decomposition, threat and vulnerability analysis, attack simulation, and risk impact analysis — aligning threat models with operational and compliance goals.",
    when: "Complex, enterprise-level systems where countermeasures must be driven by business impact.",
    chips: ["7 stages", "Attack simulation", "Business-aligned"],
  },
  {
    abbr: "STRIDE",
    icon: ShieldAlert,
    name: "Spoofing · Tampering · Repudiation · Information Disclosure · DoS · Elevation of Privilege",
    role: "Threat-identification framework",
    accent: "#38bdf8",
    desc: "Microsoft's mnemonic model for classifying threats. Every element, data flow, and trust boundary is checked against all six categories, so teams can mitigate entire classes of threats with controls tailored to each category.",
    when: "Design phase of application security, to surface threat categories against system architectures early.",
    chips: ["6 categories", "Design-phase", "Per trust boundary"],
  },
  {
    abbr: "MITRE ATT&CK",
    icon: Crosshair,
    name: "Adversary Tactics & Techniques Knowledge Base",
    role: "Adversary framework / knowledge base",
    accent: "#34d399",
    desc: "A globally maintained encyclopedia of the tactics and techniques real adversaries use — from initial access and execution to persistence and exfiltration. ThreatForge maps every modeled threat to ATT&CK techniques, anchoring abstract risks to observed attacker TTPs.",
    when: "Tying modeled threats to known attack patterns and measurable detection coverage.",
    chips: ["14 tactics", "Techniques & TTPs", "Real-world mapping"],
  },
  {
    abbr: "DREAD",
    icon: Gauge,
    name: "Damage · Reproducibility · Exploitability · Affected Users · Discoverability",
    role: "Risk-rating model",
    accent: "#fbbf24",
    desc: "A quantitative scoring model — five factors, each rated 0–10 — applied after threats are identified. Much like CVSS, DREAD turns qualitative threat lists into ranked scores teams can justify to engineering leads and executives.",
    when: "Mature security teams that need repeatable, data-driven prioritization of remediation.",
    chips: ["5 factors", "0–10 scoring", "CVSS-style ranking"],
  },
];

export default function TheQuestion() {
  return (
    <section className="tfl-questions" id="methodology" aria-label="Threat Modeling Methodologies and Frameworks">
      <div className="tfl-blob tfl-questions__blob" aria-hidden="true">
        <div className="tfl-blob__shape" />
      </div>

      <div className="tfl-frameworks">
        <div className="tfl-frameworks__header">
          <div className="tfl-eyebrow">Threat Modeling Methodologies &amp; Frameworks</div>
          <h3 className="tfl-frameworks__title">
            Engineered on proven security frameworks.
          </h3>
          <p className="tfl-frameworks__lede">
            STRIDE identifies threats, DREAD scores and prioritizes them, and PASTA models attacker
            behavior against business impact. ThreatForge composes all four into one automated
            workflow — identifying, classifying, and rating every risk in a single pass.
          </p>
        </div>

        <div className="tfl-frameworks__grid">
          {FRAMEWORKS.map((f) => {
            const IconComponent = f.icon;
            return (
              <article
                key={f.abbr}
                className="tfl-framework-card"
                style={{ "--fw-accent": f.accent }}
              >
                <div className="tfl-framework-card__top">
                  <span className="tfl-framework-card__icon">
                    <IconComponent size={20} strokeWidth={2} />
                  </span>
                  <span className="tfl-framework-card__role">{f.role}</span>
                </div>
                <h4 className="tfl-framework-card__abbr">{f.abbr}</h4>
                <div className="tfl-framework-card__name">{f.name}</div>
                <p className="tfl-framework-card__desc">{f.desc}</p>
                <div className="tfl-framework-card__when">
                  <span className="tfl-framework-card__when-label">When to use</span>
                  {f.when}
                </div>
                <ul className="tfl-framework-card__chips">
                  {f.chips.map((chip) => (
                    <li key={chip}>{chip}</li>
                  ))}
                </ul>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
